/**
 * Client Ledger Hub Page (UI-8 v3 layout, BE-11 data)
 *
 * Organization list with outstanding, wallet, net position, aging.
 * Click row -> navigate to client statement.
 * Route: /reports/client-ledger
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Alert, Button, Tooltip } from '@mui/material';
import { Search, FileDownload } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, CurrencyText } from '../../components/accounting';
import { clientLedgerService } from '../../services/accounting/clientLedger.service';
import { serverMessage } from '../../utils/apiConvert';

const COLUMNS = [
  { key: 'name', label: 'Organization', width: '28%' },
  { key: 'outstanding', label: 'Outstanding', width: '16%', align: 'right', render: (r) => <CurrencyText amount={r.outstanding} /> },
  { key: 'walletBalance', label: 'Wallet', width: '16%', align: 'right', render: (r) => <CurrencyText amount={r.walletBalance} /> },
  {
    key: 'netPosition', label: 'Net Position', width: '16%', align: 'right',
    render: (r) => {
      const net = Number(r.netPosition || 0);
      return (
        <Box>
          <CurrencyText amount={Math.abs(net)} />
          {net < 0 && (
            <Typography component="span" sx={{ fontSize: fs.xs, color: 'success.main', ml: 0.5 }}>(cr)</Typography>
          )}
        </Box>
      );
    },
  },
  {
    key: 'oldestAgeDays', label: 'Oldest Age', width: '12%', align: 'right',
    render: (r) => (
      <Typography
        sx={{
          fontSize: fs.sm,
          color: r.oldestAgeDays > 90 ? 'error.main' : (r.oldestAgeDays > 60 ? 'warning.main' : 'text.primary'),
          fontWeight: r.oldestAgeDays > 90 ? fw.semibold : fw.normal,
        }}
      >
        {r.oldestAgeDays > 0 ? `${r.oldestAgeDays}d` : '—'}
      </Typography>
    ),
  },
];

const ClientLedger = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search runs once typing pauses, not on every key.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await clientLedgerService.getClientList(search.trim() ? { search: search.trim() } : {});
        if (!cancelled) setClients(res?.data || []);
      } catch (err) {
        if (!cancelled) setError(serverMessage(err, 'Failed to load client list'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1 }}>
        Client Statements
      </Typography>

      {/* Search + Export */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          placeholder="Search organizations..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
            sx: { fontSize: fs.sm },
          }}
          sx={{ width: 280, maxWidth: '100%' }}
        />
        <Tooltip title="Coming soon">
          <span>
            <Button variant="text" size="small" startIcon={<FileDownload />} disabled sx={{ textTransform: 'none', fontSize: fs.xs }}>
              Export
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Client table */}
      <DataTable
        columns={COLUMNS}
        data={clients}
        loading={loading}
        onRowClick={(row) => navigate(`/reports/client-ledger/${row.id}`)}
        emptyMessage={search ? 'No organizations match your search.' : 'No client records found.'}
        pagination={false}
      />
    </Box>
  );
};

export default ClientLedger;
